import { externalEvents } from "@closerplatform/closer-sdk";
import {
  InboxSorting,
  UnreadState,
  GuestProfile
} from "@closerplatform/spinner-openapi";
import { logger } from "@fidget/common/utils";
import { fromPairs } from "lodash";
import { detach, flow, getSnapshot, Instance, types } from "mobx-state-tree";
import { conversationResourceDataToSnapshot, IConversationSnapshotIn } from "../base/Conversation";
import {
  currentRoomGetter,
  currentUserProfileGetter,
  roomStoreGetter,
  routerStoreGetter,
  servicesGetters,
  uiStoreGetter
} from "../utils";
import { InboxEventListener } from "./eventHandlers";
import { GlobalFilter } from "./GlobalFilter";
import { IInboxEntry, InboxEntry } from "./InboxEntry";
import {
  determineInboxEntryFilters,
  IInboxFilter,
  InboxFilter,
  InboxFilterId,
  InboxLoadingState,
  UnreadType
} from "./InboxFilter";
import { getSortingFunction } from "./sortingFunctions";

type FindCallback = (entry: IInboxEntry) => void;

const FilterReferenceType = types.reference(InboxFilter, {
  get(id: string, parent: any) {
    // FIXME: `any` to avoid self-reference issue
    const { filters } = parent;
    return filters.get(id) as IInboxFilter;
  },
  set(filter: IInboxFilter) {
    return (filter && filter.id) || "";
  }
});

const SearchResult = InboxEntry.named("SearchResult");

/**
 * Inbox model. Holds filters (tabs) which contain individual inbox entries.
 */
export const InboxStore = types
  .model("InboxStore", {
    filters: types.map(InboxFilter),
    // TODO: maybe rename it to currentFilter to stay consistent with other models?
    globalFilter: GlobalFilter,
    selectedFilter: FilterReferenceType,
    selectedSorting: types.enumeration<InboxSorting>(Object.values(InboxSorting)),
    searchResults: types.array(SearchResult),
    unassignedCount: 0,
    unassignedCountByTagGroup: types.map(types.number),
    unassignedCountNoTagGroup: 0,
  })
  .views(currentRoomGetter)
  .views(servicesGetters)
  .views(routerStoreGetter)
  .views(roomStoreGetter)
  .views(currentUserProfileGetter)
  .views(uiStoreGetter)
  .views((self) => {
    const views = {
      /**
       * Return total number of unread messages in inbox
       * @return {number} number of unread messages
       */
      get unreadCount(): number {
        return Array.from(self.filters.values()).reduce(
          (count, filter) => count + (filter.unreadCount || 0),
          0
        );
      },

      get ownUnreadCount(): number {
        return (
          (self.filters.get(InboxFilterId.YOURS)!.unreadCount || 0) +
          (self.filters.get(InboxFilterId.FOLLOWED)!.unreadCount || 0)
        );
      },

      get displayedEntries(): Array<IInboxEntry> {
        if (self.searchResults.length) {
          return self.searchResults.slice().sort(getSortingFunction(self.selectedSorting));
        }
        const entries = self.selectedFilter.loadedEntries
          .slice()
          .sort(getSortingFunction(self.selectedSorting));

        const entriesFiltered =
          self.selectedFilter.id === InboxFilterId.YOURS && !self.globalFilter.isAnyFilterEnabled
            ? entries.filter((entry: IInboxEntry) => !entry.isSnoozed)
            : entries;

        return self.globalFilter.getFilteredEntries(entriesFiltered);
      },

      get unassignedCounters() {
        return {
          count: self.unassignedCount,
          countByTagGroup: self.unassignedCountByTagGroup,
          countNoTagGroup: self.unassignedCountNoTagGroup,
        };
      },

      get yoursLength(): number {
        return self.filters.get(InboxFilterId.YOURS)?.loadedEntries.length || 0;
      },

      get snoozedEntries(): Array<IInboxEntry> {
        if (self.globalFilter.isAnyFilterEnabled) {
          return [];
        }

        const yoursEntries = self.filters.get(InboxFilterId.YOURS);
        if (yoursEntries && self.selectedFilter.id === InboxFilterId.YOURS) {
          return yoursEntries.loadedEntries.filter((entry: IInboxEntry) => entry.isSnoozed);
        }
        return [];
      },

      get permittedFilters() {
        return Array.from(self.filters.values()).filter((f) => f.isPermitted);
      }
    };
    return views;
  })
  .actions((self) => {
    let initialized = false;
    const eventListenerName = "InboxEventListener";

    const loadEntrySnapshot = async (
      roomId: string
    ): Promise<IConversationSnapshotIn | undefined> => {
      try {
        const conversation = await self.spinnerService.getConversation(roomId);
        return conversationResourceDataToSnapshot(conversation);
      } catch (e) {
        logger.error({ message: "Couldn't load inbox conversation", error: e });

        return undefined;
      }
    };

    const getEntrySnapshot = async (
      roomId: string
    ): Promise<IConversationSnapshotIn | undefined> => {
      const loadedEntries = actions.findByRoomId(roomId);
      if (loadedEntries && loadedEntries.length > 0) {
        return getSnapshot<IConversationSnapshotIn>(loadedEntries[0]!);
      }

      // TODO: Do not send requests through advisers unrelated to the given conversation.
      // These conditions reflect the adviser's role in the system – subject to potential improvement
      if (self.uiStore.canDisplayOnlyOwnConversations() && !self.uiStore.canDisplayTagGroupConversations()) {
        logger.debug(
          `Adviser: ${self.currentUserProfile?.id} is not related to room ${roomId}. No request will be sent for this inbox entry.`
        );
        return undefined;
      }

      return loadEntrySnapshot(roomId);
    };

    const actions = {
      /**
       * Setup inbox: load or reload initial entries and selected filter.
       */
      setup() {
        if (initialized === false) {
          self.filters.get(InboxFilterId.YOURS)?.loadAllEntries();
          actions.selectFilter(self.permittedFilters[0]!.id);

          if (self.selectedFilter.id !== InboxFilterId.YOURS) {
            self.selectedFilter.loadEntries();
          }
          initialized = true;
        } else {
          // clear data of not-selected filters.
          [...self.filters.values()]
            .filter((filter) => filter !== self.selectedFilter)
            .forEach((filter) => {
              filter.lastSelectedEntry = undefined;
              filter.loadedEntries.clear();
              filter.loadingState = InboxLoadingState.UNLOADED;
            });

          if (self.selectedFilter) {
            self.selectedFilter.refresh();
          }
        }
        actions.fetchStats();
      },

      invalidate: () => {
        Array.from(self.filters.values()).forEach((filter) => filter.clearEntries());
        self.roomStore.setCurrentRoom(undefined);

        self.selectedFilter.loadAllEntries();
      },

      /**
       * Switch inbox message sorting
       */
      switchSorting() {
        if (self.selectedSorting === InboxSorting.Newest) {
          actions.selectAndUpdateSorting(InboxSorting.Oldest);
        } else {
          actions.selectAndUpdateSorting(InboxSorting.Newest);
        }

        if (self.selectedFilter.pageSize > 0) {
          self.selectedFilter.clearEntries();
          self.selectedFilter.loadEntries();
        }
      },

      /**
       * Select inbox message sorting & update in agent's preferences at spinner
       */
      selectAndUpdateSorting(sorting: InboxSorting) {
        actions.selectSorting(sorting);
        self.spinnerService.patchAgentPreferences({
          inboxSorting: sorting
        });
      },

      /**
       * Select inbox message sorting
       */
      selectSorting(sorting: InboxSorting) {
        self.selectedSorting = sorting;
      },

      /**
       * Select filter
       */
      selectFilter(id: string) {
        const filter = self.filters.get(id);
        if (filter) {
          self.selectedFilter = filter;

          if (
            filter.requiresReload ||
            (filter.id !== InboxFilterId.YOURS && filter.id !== InboxFilterId.FOLLOWED)
          ) {
            filter.refresh();
          }
        }
      },

      /**
       * Load selected search result
       */
      selectSearchResult: flow(function* selectSearchResult(profile: GuestProfile) {
        const snapshot: IConversationSnapshotIn | undefined = yield loadEntrySnapshot(profile.roomId);
        if (snapshot && !self.searchResults.find((a) => a.id === snapshot.id)) {
          self.searchResults.push(snapshot);
        }
        self.routerStore.goToRoom(profile.roomId);
      }),

      setUnassignedCounters(
        count: number,
        countByTagGroup: Record<string, number>,
        countNoTagGroup: number
      ) {
        self.unassignedCount = count;
        self.unassignedCountByTagGroup.clear();
        Object.keys(countByTagGroup).forEach((k) => self.unassignedCountByTagGroup.set(k, countByTagGroup[k]!));
        self.unassignedCountNoTagGroup = countNoTagGroup;
      },

      /**
       * Clear search results. Set room to previously selected entry if there is one.
       */
      clearSearch() {
        if (self.selectedFilter.lastSelectedEntry) {
          self.routerStore.goToRoom(self.selectedFilter.lastSelectedEntry.roomId);
        }
        self.searchResults.clear();
      },

      /**
       * Ensure inbox entry is in the right filter.
       * Call this action after changing any of the properties that contribute
       * to filter qualification or receiving information about a new conversation.
       * TODO: ensure entry is GCd
       * @param {string} roomId entry's room id
       */
      reassignEntry: flow(function* reassignEntry(entry: IConversationSnapshotIn) {
        const user = self.currentUserProfile;

        try {
          const targetFilterIds = determineInboxEntryFilters(
            entry,
            user && user.id,
            user && user.skills
          );

          self.filters.forEach((filter) => {
            const entryNode = filter.loadedEntries.find(
              (e: IInboxEntry) => e.roomId === entry.roomId
            );
            const entryBelongsInFilter = targetFilterIds.includes(filter.id as InboxFilterId);
            const isAllowed = () =>
              filter.isWholePermitted ||
              (self.uiStore.canDisplayTagGroupConversations() &&
                (!entry.owner.tagGroupId ||
                  self.currentUserProfile?.hasTagGroup(entry.owner.tagGroupId)));

            if (entryNode && !entryBelongsInFilter) {
              entryNode.unsubscribeRoomListeners();
              detach(entryNode);
              filter.recalculateUnreads();
            } else if (!entryNode && entryBelongsInFilter && isAllowed()) {
              filter.insertEntry(entry);
            }
          });
        } catch (error) {
          logger.error({ message: "Couldn't reassign inbox entry", error });
          throw new Error("Could not reassign conversation");
        }
      }),

      reassignEntryByRoomId: flow(function* (roomId: string) {
        const snapshot: IConversationSnapshotIn | undefined = yield getEntrySnapshot(roomId);
        if (snapshot) {
          actions.reassignEntry(snapshot);
        }
      }),

      reloadReassignEntryByRoomId: flow(function* (roomId: string) {
        const snapshot: IConversationSnapshotIn | undefined = yield loadEntrySnapshot(roomId);
        if (snapshot) {
          actions.reassignEntry(snapshot);
        }
      }),

      /**
       * Get the already loaded inbox entry by room id.
       * If exists, execute optional callback, otherwise skip silently.
       * @param roomId room id to search with
       * @param execute the callback.
       */
      findByRoomId(roomId: string, callback?: FindCallback): Array<IInboxEntry> | undefined {
        let found: IInboxEntry[] = [];
        self.filters.forEach((filter) => {
          found = found.concat(
            filter.loadedEntries.filter((entry: IInboxEntry) => entry.roomId === roomId)
          );
        });

        if (callback) {
          found.forEach((entry) => callback(entry));
        }
        return found.length ? found : undefined;
      },

      /**
       * Get the already loaded inbox entry by owner id.
       * If exists, execute optional callback, otherwise skip silently.
       * @param ownerId owner id to search with
       * @param execute the callback.
       */
      findByOwnerId(ownerId: string, callback?: FindCallback): Array<IInboxEntry> | undefined {
        let found: IInboxEntry[] = [];
        self.filters.forEach((filter) => {
          found = found.concat(
            Array.from(filter.loadedEntries.values()).filter(
              (e: IInboxEntry) => e.owner.id === ownerId
            )
          );
        });
        if (callback) {
          found.forEach((entry) => callback(entry));
        }
        return found.length ? found : undefined;
      },

      updateWaitingCounters(
        unreadCount: number,
        unreadCountNoTagGroup?: number,
        unreadCountByTagGroup?: NonNullable<
          externalEvents.UnreadTotalUpdated["unreadCountByTagGroup"]
        >
      ) {
        const calculateUnread = () => {
          if (
            !self.uiStore.canDisplayWaitingConversations() &&
            self.uiStore.canDisplayTagGroupConversations() &&
            unreadCountNoTagGroup !== undefined &&
            unreadCountByTagGroup !== undefined
          ) {
            const matchingEntries = self.currentUserProfile!.allTagGroupIds.reduce(
              (acc, tg) => acc + unreadCountByTagGroup[tg]! || 0,
              0
            );

            return unreadCountNoTagGroup + matchingEntries;
          }

          return unreadCount;
        };

        const unread = calculateUnread();
        self.filters.get(InboxFilterId.WAITING)?.setUnreadCount(unread);

        return unread;
      },

      updateUnassignedCounters(
        count: number,
        countByTagGroup: Record<string, number>,
        countNoTagGroup: number,
      ) {
        const ownTagGroupIds = self.currentUserProfile!.allTagGroupIds;
        const ownCountByTagGroup: typeof countByTagGroup = ownTagGroupIds.reduce((acc, tg) => ({
          ...acc,
          ...(countByTagGroup[tg] ? {[tg]: countByTagGroup[tg]} : {})
        }), {});
        return actions.setUnassignedCounters(
          ownTagGroupIds.length === 0
            ? count
            : countNoTagGroup + Object.values(ownCountByTagGroup).reduce((a, b) => a + b, 0),
          ownCountByTagGroup,
          countNoTagGroup
        );
      },

      /**
       * Fetch filter total unread counts from backend and update the filters
       */
      fetchStats: flow(function* fetchFilterStats() {
        try {
          const data: UnreadState = yield self.spinnerService.getUnreadCounters();

          self.filters.get(InboxFilterId.YOURS)?.setUnreadCount(data.yours);
          self.filters.get(InboxFilterId.FOLLOWED)?.setUnreadCount(data.followed);

          actions.updateUnassignedCounters(
            data.unassigned,
            data.unassignedByTagGroup.reduce((acc, e) => ({...acc, [e.tagGroupId]: e.count}), {}),
            data.unassignedWithNoTagGroup
          );

          actions.updateWaitingCounters(
            data.waiting,
            data.waitingWithNoTagGroup,
            fromPairs(data.waitingByTagGroup.map((i) => [i.tagGroupId, i.count]))
          );
        } catch (error) {
          logger.error({ message: "Couldn't fetch inbox stats", error });
          throw new Error(error as string);
        }
      }),

      afterCreate() {
        self.eventListener.addGlobalListener(
          new InboxEventListener(() => self as IInboxStore, eventListenerName)
        );
      },

      beforeDestroy() {
        self.eventListener.removeGlobalListener(eventListenerName);
      }
    };

    return actions;
  });

export interface IInboxStore extends Instance<typeof InboxStore> {}

// XXX: filter order is maintained thanks to the way MST processes snapshot properties
export const INBOX_INITIAL_DATA = {
  filters: {
    [InboxFilterId.YOURS]: { id: InboxFilterId.YOURS, unreadType: UnreadType.MESSAGES },
    [InboxFilterId.FOLLOWED]: { id: InboxFilterId.FOLLOWED, unreadType: UnreadType.MESSAGES },
    [InboxFilterId.WAITING]: { id: InboxFilterId.WAITING, unreadType: UnreadType.ENTRIES },
    [InboxFilterId.IN_PROGRESS]: { id: InboxFilterId.IN_PROGRESS },
    [InboxFilterId.CLOSED]: { id: InboxFilterId.CLOSED, pageSize: 25 }
  },
  selectedFilter: InboxFilterId.YOURS,
  selectedSorting: InboxSorting.Newest,
  globalFilter: {}
};

export { InboxFilter, InboxFilterId, InboxEntry };
